import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";

const TURNSTILE_SCRIPT_ID = "cf-turnstile-script";
const TURNSTILE_SCRIPT_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

function toFriendlyCaptchaMessage(error, fallback) {
  const raw = String(error?.message || "").toLowerCase();

  if (raw.includes("timed out") || raw.includes("timeout")) {
    return "Captcha timed out. Please try the checkbox again.";
  }

  if (
    raw.includes("invalid value for parameter") ||
    raw.includes("turnstile") ||
    raw.includes("captcha")
  ) {
    return fallback;
  }

  return fallback;
}

function ensureTurnstileScript() {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("Turnstile is only available in browser."));
  }

  if (window.turnstile) {
    return Promise.resolve(window.turnstile);
  }

  const existingScript = document.getElementById(TURNSTILE_SCRIPT_ID);
  if (existingScript) {
    return new Promise((resolve, reject) => {
      existingScript.addEventListener("load", () => resolve(window.turnstile), {
        once: true,
      });
      existingScript.addEventListener(
        "error",
        () => reject(new Error("Failed to load Turnstile script.")),
        { once: true }
      );
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.id = TURNSTILE_SCRIPT_ID;
    script.src = TURNSTILE_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.turnstile);
    script.onerror = () => reject(new Error("Failed to load Turnstile script."));
    document.head.appendChild(script);
  });
}

const TurnstileWidget = forwardRef(function TurnstileWidget(
  {
    className = "",
    onTokenChange,
    onError,
    resetKey = 0,
    theme = "light",
    size = "normal",
    action,
    mode = "managed",
    requireManualInteraction = true,
  },
  ref
) {
  const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY;
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [loadError, setLoadError] = useState(null);
  const onTokenChangeRef = useRef(onTokenChange);
  const onErrorRef = useRef(onError);
  const pendingResolveRef = useRef(null);
  const pendingRejectRef = useRef(null);
  const pendingTimeoutRef = useRef(null);
  const pendingPromiseRef = useRef(null);

  const isInvisibleMode = mode === "invisible";
  const isManualMode = mode === "manual";
  const widgetSize = isInvisibleMode ? "normal" : size;

  useEffect(() => {
    onTokenChangeRef.current = onTokenChange;
  }, [onTokenChange]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  const clearPendingExecution = () => {
    if (pendingTimeoutRef.current) {
      clearTimeout(pendingTimeoutRef.current);
      pendingTimeoutRef.current = null;
    }
    pendingResolveRef.current = null;
    pendingRejectRef.current = null;
    pendingPromiseRef.current = null;
  };

  useImperativeHandle(
    ref,
    () => ({
      async execute() {
        if (!window.turnstile || widgetIdRef.current === null) {
          throw new Error("Captcha is not ready yet. Please try again.");
        }

        const existingToken = window.turnstile.getResponse(widgetIdRef.current);
        if (existingToken) {
          return existingToken;
        }

        if (pendingPromiseRef.current) {
          return pendingPromiseRef.current;
        }

        pendingPromiseRef.current = new Promise((resolve, reject) => {
          pendingResolveRef.current = resolve;
          pendingRejectRef.current = reject;

          pendingTimeoutRef.current = setTimeout(() => {
            if (pendingRejectRef.current) {
              pendingRejectRef.current(new Error("Captcha verification timed out."));
            }
            clearPendingExecution();
          }, 15000);

          try {
            window.turnstile.execute(widgetIdRef.current);
          } catch {
            clearPendingExecution();
            reject(new Error("Captcha verification could not be completed. Please try again."));
          }
        });

        return pendingPromiseRef.current;
      },
      reset() {
        if (!window.turnstile || widgetIdRef.current === null) return;
        window.turnstile.reset(widgetIdRef.current);
        onTokenChangeRef.current?.("");
      },
    }),
    []
  );

  useEffect(() => {
    if (!siteKey || !containerRef.current) return undefined;

    let isUnmounted = false;

    const mountWidget = async () => {
      try {
        const turnstile = await ensureTurnstileScript();
        if (isUnmounted || !turnstile || !containerRef.current) return;

        widgetIdRef.current = turnstile.render(containerRef.current, {
          sitekey: siteKey,
          theme,
          size: widgetSize,
          action,
          execution: isInvisibleMode || isManualMode ? "execute" : "render",
          appearance: isInvisibleMode ? "execute" : "always",
          retry: "auto",
          "refresh-expired": "manual",
          "refresh-timeout": "manual",
          callback: (token) => {
            onTokenChangeRef.current?.(token || "");
            if (pendingResolveRef.current) {
              pendingResolveRef.current(token || "");
              clearPendingExecution();
            }
          },
          "expired-callback": () => {
            onTokenChangeRef.current?.("");
            if (pendingRejectRef.current) {
              pendingRejectRef.current(new Error("Captcha expired. Please try again."));
              clearPendingExecution();
            }
          },
          "error-callback": () => {
            const existingToken =
              widgetIdRef.current !== null
                ? window.turnstile?.getResponse(widgetIdRef.current)
                : "";

            if (existingToken) {
              onTokenChangeRef.current?.(existingToken);
              return;
            }

            onTokenChangeRef.current?.("");
            if (pendingRejectRef.current) {
              pendingRejectRef.current(new Error("Captcha error. Please try again."));
              clearPendingExecution();
            }
            onErrorRef.current?.("Captcha error. Please refresh and try again.");
          },
          "timeout-callback": () => {
            const existingToken =
              widgetIdRef.current !== null
                ? window.turnstile?.getResponse(widgetIdRef.current)
                : "";

            if (existingToken) {
              onTokenChangeRef.current?.(existingToken);
              return;
            }

            onTokenChangeRef.current?.("");
            // Timeout is an expected user state; clear token quietly and let user retry.
          },
        });
      } catch (error) {
        if (isUnmounted) return;
        const friendlyMessage = toFriendlyCaptchaMessage(
          error,
          "Unable to load captcha right now. Please refresh and try again."
        );
        setLoadError(friendlyMessage);
        onErrorRef.current?.(friendlyMessage);
      }
    };

    mountWidget();

    return () => {
      isUnmounted = true;
      clearPendingExecution();
      if (window.turnstile && widgetIdRef.current !== null) {
        window.turnstile.remove(widgetIdRef.current);
      }
      widgetIdRef.current = null;
    };
  }, [action, isInvisibleMode, isManualMode, requireManualInteraction, siteKey, theme, widgetSize]);

  useEffect(() => {
    if (!siteKey || widgetIdRef.current === null || !window.turnstile) return;

    window.turnstile.reset(widgetIdRef.current);
    onTokenChangeRef.current?.("");
  }, [resetKey, siteKey]);

  if (!siteKey) {
    return null;
  }

  return (
    <div className={className}>
      <div ref={containerRef} />
      {loadError && <p className="mt-2 text-sm text-rose-500">{loadError}</p>}
    </div>
  );
});

export default TurnstileWidget;
